import { useState, useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useTenant } from '../../contexts/TenantContext';
import { logger } from '../../utils/logger';
import { jetonTempsReel } from '../../utils/jetonTempsReel';

// Remove /api suffix if present for WebSocket connection
const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');

export interface QueueEntry {
  id: string;
  customer_id: string;
  customer_name: string;
  position: number;
  created_at: string;
  wait_time_minutes: number;
}

export interface VendorStatus {
  vendor_id: string;
  vendor_name: string;
  is_available: boolean;
  is_in_session: boolean;
  session_id: string | null;
}

export interface ActiveSession {
  id: string;
  tenant_id: string;
  customer_id: string;
  vendor_id: string;
  status: 'waiting' | 'active' | 'checkout';
  cart_snapshot: { items: CartItem[] };
  total_cents: number;
  started_at?: string;
  created_at: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone?: string;
  } | null;
  item_count: number;
}

export interface LiveSession {
  id: string;
  tenant_id: string;
  customer_id: string;
  vendor_id: string;
  status: 'scheduled' | 'waiting' | 'active' | 'checkout' | 'completed' | 'cancelled';
  cart_snapshot: { items: CartItem[] };
  total_cents: number;
  started_at?: string;
  ended_at?: string;
}

export interface CartItem {
  product_id: string;
  name: string;
  quantity: number;
  unit?: string;
  price_cents: number;
  vat_rate?: number;
}

export interface ChatMessage {
  from: 'vendor' | 'customer';
  message: string;
  timestamp: string;
  suggestions?: any[];
}

export interface CustomerSuggestion {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit?: string;
  price_cents: number;
  image_url?: string;
  timestamp: string;
  status: 'pending' | 'approved' | 'rejected' | 'later';
}

// Types pour l'intégration avec useEcommerceCart
export interface EcommerceCartAction {
  type: 'add' | 'remove' | 'update';
  product?: any; // Product complet pour 'add'
  productId?: string; // Pour 'remove' et 'update'
  quantity?: number; // Pour 'add' et 'update'
}

export interface EcommerceCartItem {
  product: any;
  quantity: number;
  unit_price_cents: number;
  total_price_cents: number;
  prepared: boolean;
  addedBy: 'customer' | 'vendor';
  addedAt: string;
}

interface UseLiveSessionOptions {
  role: 'vendor' | 'customer';
  vendorId?: string;
  customerId?: string;
  // Session ID initial (from URL params) - permet de gérer rejoin_session_required correctement
  // avant même que joinSession ne soit appelé
  initialSessionId?: string;
  onSessionAssigned?: (sessionId: string, vendorId: string) => void;
  onQueueUpdated?: (queue: QueueEntry[]) => void;
  onCartUpdated?: (cart: { items: CartItem[] }) => void;
  onMessage?: (message: ChatMessage) => void;
  onStatusChanged?: (status: string) => void;
  onSuggestion?: (suggestion: CustomerSuggestion) => void;
  onSuggestionResponse?: (suggestionId: string, action: 'approve' | 'reject' | 'later') => void;
  // Intégration E-commerce Cart
  onCartAction?: (action: EcommerceCartAction) => void; // Client reçoit action du vendeur
  onCartStateSync?: (items: EcommerceCartItem[]) => void; // Vendeur reçoit état panier client
  onVendorJoined?: () => void; // Client: vendeur a rejoint, re-synchroniser le panier
  // Événement quand le vendeur termine sa session
  onVendorSessionEnded?: (data: {
    preparedItemsCount: number;
    totalPreparedCents: number;
    orderNumber?: string; // Numéro de commande pré-réservé (CMD-xxx)
    message: string;
  }) => void;
}

interface UseLiveSessionReturn {
  // Connection
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;

  // Vendor availability
  isAvailable: boolean;
  setAvailable: (available: boolean) => Promise<void>;
  availableVendors: VendorStatus[];

  // Active sessions (vendor can rejoin)
  activeSessions: ActiveSession[];
  refreshActiveSessions: () => Promise<void>;

  // Queue management
  queue: QueueEntry[];
  queuePosition: number | null;
  estimatedWaitMinutes: number | null;
  joinQueue: () => Promise<void>;
  leaveQueue: () => Promise<void>;
  acceptQueueEntry: (entryId: string) => Promise<{ sessionId: string; customerId: string }>;

  // Session
  session: LiveSession | null;
  sessionId: string | null;
  joinSession: (sessionId: string) => Promise<void>;

  // Cart
  cart: { items: CartItem[] };
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  updateCartItem: (productId: string, quantity: number) => void;
  clearCart: () => void;

  // Chat
  messages: ChatMessage[];
  sendMessage: (message: string, suggestions?: any[]) => void;

  // Customer suggestions (bidirectional cart)
  suggestions: CustomerSuggestion[];
  suggestProduct: (product: Omit<CustomerSuggestion, 'id' | 'timestamp' | 'status'>) => void;
  respondToSuggestion: (suggestionId: string, action: 'approve' | 'reject' | 'later') => void;

  // E-commerce cart integration
  sendCartAction: (action: EcommerceCartAction) => void; // Vendeur envoie action au client
  syncCartState: (items: EcommerceCartItem[], forceSessionId?: string) => void; // Client sync son panier au vendeur
  vendorCartState: EcommerceCartItem[]; // État du panier client vu par le vendeur

  // Session control
  requestCheckout: () => Promise<void>;
  completeSession: (cartItems?: EcommerceCartItem[]) => Promise<{
    success: boolean;
    orderId?: string;
    orderNumber?: string;
    totalCents?: number;
    error?: string;
  }>;
  cancelSession: () => Promise<void>;

  // Session control with fees
  getCancellationFees: () => Promise<{
    feesCents: number;
    durationMinutes: number;
    hasItems: boolean;
    feesEuros: string;
  } | null>;
  cancelSessionWithFees: (
    cancelledBy?: 'customer' | 'vendor',
    reason?: 'manual' | 'technical'
  ) => Promise<{
    success: boolean;
    feesCents: number;
    durationMinutes: number;
  } | null>;
  finalizeSessionWithoutCustomer: () => Promise<{
    success: boolean;
    orderId: string;
    orderNumber: string;
  } | null>;

  // Vendeur termine sa session (imprime ticket articles préparés, libère le vendeur)
  endVendorSession: (vendorName?: string) => Promise<{
    success: boolean;
    preparedItemsCount: number;
    totalPreparedCents: number;
    printSuccess?: boolean;
    error?: string;
  } | null>;
}

export function useLiveSession(options: UseLiveSessionOptions): UseLiveSessionReturn {
  const {
    role,
    vendorId,
    customerId,
    initialSessionId,
    onSessionAssigned,
    onQueueUpdated,
    onCartUpdated,
    onMessage,
    onStatusChanged,
    onSuggestion,
    onSuggestionResponse,
    onCartAction,
    onCartStateSync,
    onVendorJoined,
    onVendorSessionEnded,
  } = options;

  const { tenant } = useTenant();
  const tenantId = tenant?.id || '';

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isAvailable, setIsAvailableState] = useState(false);
  const [availableVendors, setAvailableVendors] = useState<VendorStatus[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [estimatedWaitMinutes, setEstimatedWaitMinutes] = useState<number | null>(null);
  const [session, setSession] = useState<LiveSession | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [cart, setCart] = useState<{ items: CartItem[] }>({ items: [] });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<CustomerSuggestion[]>([]);
  const [vendorCartState, setVendorCartState] = useState<EcommerceCartItem[]>([]);

  // Refs
  const socketRef = useRef<Socket | null>(null);
  const isMountedRef = useRef(false);
  // Initialize with initialSessionId to handle rejoin_session_required before joinSession is called
  const sessionIdRef = useRef<string | null>(initialSessionId || null);
  const callbacksRef = useRef({
    onQueueUpdated,
    onSessionAssigned,
    onCartUpdated,
    onMessage,
    onStatusChanged,
    onSuggestion,
    onSuggestionResponse,
    onCartAction,
    onCartStateSync,
    onVendorJoined,
    onVendorSessionEnded,
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onQueueUpdated,
      onSessionAssigned,
      onCartUpdated,
      onMessage,
      onStatusChanged,
      onSuggestion,
      onSuggestionResponse,
      onCartAction,
      onCartStateSync,
      onVendorJoined,
      onVendorSessionEnded,
    };
  }, [onQueueUpdated, onSessionAssigned, onCartUpdated, onMessage, onStatusChanged, onSuggestion, onSuggestionResponse, onCartAction, onCartStateSync, onVendorJoined, onVendorSessionEnded]);

  // Keep sessionIdRef in sync with sessionId state AND initialSessionId
  useEffect(() => {
    // Update ref when sessionId state changes OR when initialSessionId is provided
    // This ensures the ref is set before any WebSocket events fire
    if (sessionId) {
      sessionIdRef.current = sessionId;
    } else if (initialSessionId) {
      sessionIdRef.current = initialSessionId;
    }
  }, [sessionId, initialSessionId]);

  // Also set immediately when initialSessionId is provided (before first effect runs)
  if (initialSessionId && !sessionIdRef.current) {
    sessionIdRef.current = initialSessionId;
  }

  // Connect to WebSocket
  const connect = useCallback(async () => {
    // Skip if tenant not yet loaded
    if (!tenantId) {
      return;
    }

    // Skip if already connected
    if (socketRef.current?.connected) {
      return;
    }

    // If socket exists but not connected, try to reconnect
    if (socketRef.current) {
      socketRef.current.connect();
      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Reconnection timeout')), 10000);
        socketRef.current!.once('connect', () => {
          clearTimeout(timeout);
          setIsConnected(true);
          resolve();
        });
        socketRef.current!.once('connect_error', (err) => {
          clearTimeout(timeout);
          reject(err);
        });
      });
      return;
    }

    const socket = io(`${API_URL}/live`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: { token: jetonTempsReel() },
    });

    socketRef.current = socket;

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Connection timeout')), 10000);

      socket.on('connect', () => {
        clearTimeout(timeout);
        setIsConnected(true);
        logger.log('[useLiveSession] ✅ WebSocket connected successfully');
        resolve();
      });

      socket.on('connect_error', (err) => {
        clearTimeout(timeout);
        reject(err);
      });
    });

    // Set up event listeners
    socket.on('vendor_status', (data: VendorStatus) => {
      logger.log('[useLiveSession] Vendor status changed:', data);
      setAvailableVendors((prev) => {
        const existing = prev.findIndex((v) => v.vendor_id === data.vendor_id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = data;
          logger.log('[useLiveSession] Updated existing vendor in list:', data.vendor_id, 'Available:', data.is_available, 'In Session:', data.is_in_session);
          return updated;
        }
        const newList = data.is_available ? [...prev, data] : prev;
        logger.log('[useLiveSession] Vendor list updated, available count:', newList.filter(v => v.is_available && !v.is_in_session).length);
        return newList;
      });
    });

    socket.on('queue_updated', (data: { action: string; entry?: QueueEntry; queue?: QueueEntry[] }) => {
      if (data.queue) {
        setQueue(data.queue);
        callbacksRef.current.onQueueUpdated?.(data.queue);
      } else if (data.action === 'new_entry' && data.entry) {
        setQueue((prev) => {
          const newQueue = [...prev, data.entry!];
          callbacksRef.current.onQueueUpdated?.(newQueue);
          return newQueue;
        });
      } else if (data.action === 'entry_removed' || data.action === 'entry_accepted') {
        // Refresh queue
        socket.emit('get_queue', { tenant_id: tenantId }, (res: { queue: QueueEntry[] }) => {
          if (res.queue) {
            setQueue(res.queue);
            callbacksRef.current.onQueueUpdated?.(res.queue);
          }
        });
      }
    });

    socket.on('position_updated', (data: { position: number; estimated_wait: number }) => {
      setQueuePosition(data.position);
      setEstimatedWaitMinutes(data.estimated_wait);
    });

    socket.on('session_assigned', (data: { session_id: string; vendor_id: string }) => {
      setSessionId(data.session_id);
      callbacksRef.current.onSessionAssigned?.(data.session_id, data.vendor_id);
    });

    socket.on('cart_updated', (data: { cart: { items: CartItem[] } }) => {
      setCart(data.cart);
      callbacksRef.current.onCartUpdated?.(data.cart);
    });

    socket.on('vendor_message', (data: { message: string; suggestions?: any[]; timestamp: string }) => {
      const msg: ChatMessage = { from: 'vendor', ...data };
      setMessages((prev) => [...prev, msg]);
      callbacksRef.current.onMessage?.(msg);
    });

    socket.on('customer_message', (data: { message: string; timestamp: string }) => {
      const msg: ChatMessage = { from: 'customer', ...data };
      setMessages((prev) => [...prev, msg]);
      callbacksRef.current.onMessage?.(msg);
    });

    socket.on('session_status_changed', (data: { status: string }) => {
      setSession((prev) => (prev ? { ...prev, status: data.status as any } : null));
      callbacksRef.current.onStatusChanged?.(data.status);
    });

    socket.on('checkout_ready', () => {
      setSession((prev) => (prev ? { ...prev, status: 'checkout' } : null));
    });

    // Customer suggestions (bidirectional cart)
    socket.on('customer_suggestion', (data: { suggestion: CustomerSuggestion }) => {
      setSuggestions((prev) => [...prev, data.suggestion]);
      callbacksRef.current.onSuggestion?.(data.suggestion);
    });

    socket.on('suggestion_response', (data: { suggestion_id: string; action: 'approve' | 'reject' | 'later' }) => {
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === data.suggestion_id ? { ...s, status: data.action === 'approve' ? 'approved' : data.action === 'reject' ? 'rejected' : 'later' } : s
        )
      );
      callbacksRef.current.onSuggestionResponse?.(data.suggestion_id, data.action);
    });

    // E-commerce cart integration
    // Client receives cart actions from vendor
    socket.on('cart_action', (data: { action: EcommerceCartAction; from: string; timestamp: string }) => {
      logger.log('[useLiveSession] 🛒 Received cart_action:', data.action.type, data.action);
      callbacksRef.current.onCartAction?.(data.action);
    });

    // Vendor receives cart state from client
    socket.on('cart_state', (data: { items: EcommerceCartItem[]; timestamp: string }) => {
      logger.log('[useLiveSession] 📦 Received cart_state:', data.items.length, 'items');
      setVendorCartState(data.items);
      callbacksRef.current.onCartStateSync?.(data.items);
    });

    // Client: when vendor joins, request cart resync from client
    // This ensures vendor has latest cart state
    socket.on('participant_joined', (data: { role: string; socket_id: string }) => {
      if (role === 'customer' && data.role === 'vendor') {
        // Vendor just joined - trigger cart resync via callback
        callbacksRef.current.onVendorJoined?.();
      }
    });

    // Request cart resync from server when vendor needs fresh cart data
    socket.on('request_cart_sync', () => {
      callbacksRef.current.onVendorJoined?.();
    });

    // Le vendeur a terminé sa session (article préparés imprimés)
    // Le client peut continuer vers le checkout classique
    socket.on('vendor_session_ended', (data: {
      session_id: string;
      prepared_items_count: number;
      total_prepared_cents: number;
      order_number?: string; // Numéro de commande pré-réservé (CMD-xxx)
      message: string;
    }) => {
      callbacksRef.current.onVendorSessionEnded?.({
        preparedItemsCount: data.prepared_items_count,
        totalPreparedCents: data.total_prepared_cents,
        orderNumber: data.order_number,
        message: data.message,
      });
    });

    // Vendor receives request to rejoin session (when customer reconnects)
    // Only auto-rejoin if vendor is already in a session (on session page)
    // If on dashboard (no sessionId), just refresh active sessions list
    socket.on('rejoin_session_required', (data: { session_id: string; reason: string }) => {
      // Use ref to get current value (closure would have stale value)
      const currentSessionId = sessionIdRef.current;

      // Check if vendor is currently in this session (on session page)
      // Either sessionIdRef matches OR it's the initialSessionId we were provided
      if (currentSessionId && currentSessionId === data.session_id) {
        // Vendor is on session page for THIS session - auto-rejoin the session room
        socket.emit(
          'join_session',
          {
            tenant_id: tenantId,
            session_id: data.session_id,
            role,
            vendor_id: vendorId,
            customer_id: customerId,
          },
          (res: { success: boolean; error?: string }) => {
            if (res.success) {
              setSessionId(data.session_id);
            }
          }
        );
      } else if (!currentSessionId) {
        // Vendor is on dashboard - just refresh the active sessions list
        if (role === 'vendor' && vendorId) {
          socket.emit(
            'get_active_sessions',
            { tenant_id: tenantId, vendor_id: vendorId },
            (res: { success: boolean; sessions?: ActiveSession[] }) => {
              if (res.success && res.sessions) {
                setActiveSessions(res.sessions);
              }
            }
          );
        }
      }
    });

    // If vendor, get initial queue and active sessions
    if (role === 'vendor' && vendorId) {
      socket.emit('get_queue', { tenant_id: tenantId }, (res: { queue: QueueEntry[] }) => {
        if (res.queue) setQueue(res.queue);
      });
      // Get active sessions that vendor can rejoin
      socket.emit(
        'get_active_sessions',
        { tenant_id: tenantId, vendor_id: vendorId },
        (res: { success: boolean; sessions?: ActiveSession[] }) => {
          if (res.success && res.sessions) {
            setActiveSessions(res.sessions);
          }
        }
      );
    }

    // Get available vendors
    socket.emit('get_available_vendors', { tenant_id: tenantId }, (res: { vendors: VendorStatus[] }) => {
      logger.log('[useLiveSession] Received available vendors:', res.vendors);
      if (res.vendors) {
        setAvailableVendors(res.vendors);
        const availableCount = res.vendors.filter(v => v.is_available && !v.is_in_session).length;
        logger.log('[useLiveSession] Total vendors:', res.vendors.length, 'Available:', availableCount);
      }
    });

  }, [tenantId, role]);

  // Auto-connect when tenant becomes available
  useEffect(() => {
    if (tenantId && !socketRef.current?.connected) {
      connect().catch(() => {
        // Auto-connect failed
      });
    }
  }, [tenantId, connect]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);
    setMessages([]);
  }, []);

  // Set vendor availability
  const setAvailable = useCallback(async (available: boolean) => {
    if (!socketRef.current || !vendorId) return;

    return new Promise<void>((resolve, reject) => {
      socketRef.current!.emit(
        'vendor_set_status',
        {
          tenant_id: tenantId,
          vendor_id: vendorId,
          is_available: available,
        },
        (res: { success: boolean; error?: string }) => {
          if (res.success) {
            setIsAvailableState(available);
            resolve();
          } else {
            reject(new Error(res.error));
          }
        }
      );
    });
  }, [tenantId, vendorId]);

  // Refresh active sessions (vendor can rejoin)
  const refreshActiveSessions = useCallback(async () => {
    if (!socketRef.current || !vendorId || !tenantId) return;

    return new Promise<void>((resolve) => {
      socketRef.current!.emit(
        'get_active_sessions',
        {
          tenant_id: tenantId,
          vendor_id: vendorId,
        },
        (res: { success: boolean; sessions?: ActiveSession[]; error?: string }) => {
          if (res.success && res.sessions) {
            setActiveSessions(res.sessions);
          }
          resolve();
        }
      );
    });
  }, [tenantId, vendorId]);

  // Join queue (customer)
  const joinQueue = useCallback(async () => {
    if (!socketRef.current || !customerId) return;

    return new Promise<void>((resolve, reject) => {
      socketRef.current!.emit(
        'join_queue',
        {
          tenant_id: tenantId,
          customer_id: customerId,
        },
        (res: { success: boolean; position?: number; error?: string }) => {
          if (res.success) {
            setQueuePosition(res.position || 1);
            resolve();
          } else {
            reject(new Error(res.error));
          }
        }
      );
    });
  }, [tenantId, customerId]);

  // Leave queue (customer)
  const leaveQueue = useCallback(async () => {
    if (!socketRef.current || !customerId) return;

    return new Promise<void>((resolve) => {
      socketRef.current!.emit(
        'leave_queue',
        {
          tenant_id: tenantId,
          customer_id: customerId,
        },
        () => {
          setQueuePosition(null);
          setEstimatedWaitMinutes(null);
          resolve();
        }
      );
    });
  }, [tenantId, customerId]);

  // Accept queue entry (vendor)
  const acceptQueueEntry = useCallback(async (entryId: string) => {
    if (!socketRef.current || !vendorId) {
      throw new Error('Not connected');
    }

    return new Promise<{ sessionId: string; customerId: string }>((resolve, reject) => {
      socketRef.current!.emit(
        'accept_queue_entry',
        {
          tenant_id: tenantId,
          vendor_id: vendorId,
          queue_entry_id: entryId,
        },
        (res: { success: boolean; sessionId?: string; customerId?: string; error?: string }) => {
          if (res.success && res.sessionId && res.customerId) {
            setSessionId(res.sessionId);
            resolve({ sessionId: res.sessionId, customerId: res.customerId });
          } else {
            reject(new Error(res.error || 'Failed to accept'));
          }
        }
      );
    });
  }, [tenantId, vendorId]);

  // Join session
  const joinSession = useCallback(async (sid: string) => {
    logger.log(`[useLiveSession] joinSession called for session: ${sid}, role: ${role}`);

    if (!socketRef.current) {
      logger.log('[useLiveSession] ❌ socketRef.current is null, cannot join session');
      return;
    }

    logger.log(`[useLiveSession] Socket connected: ${socketRef.current.connected}`);

    if (!socketRef.current.connected) {
      logger.log('[useLiveSession] Socket not connected, attempting to connect...');
      socketRef.current.connect();
      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Reconnection timeout')), 5000);
        socketRef.current!.once('connect', () => {
          clearTimeout(timeout);
          logger.log('[useLiveSession] ✅ Socket reconnected successfully');
          resolve();
        });
        socketRef.current!.once('connect_error', (err) => {
          clearTimeout(timeout);
          logger.log('[useLiveSession] ❌ Socket reconnection failed:', err);
          reject(err);
        });
      });
    }

    logger.log(`[useLiveSession] Emitting join_session event with data:`, {
      tenant_id: tenantId,
      session_id: sid,
      role,
      vendor_id: vendorId,
      customer_id: customerId,
    });

    return new Promise<void>((resolve, reject) => {
      socketRef.current!.emit(
        'join_session',
        {
          tenant_id: tenantId,
          session_id: sid,
          role,
          vendor_id: vendorId,
          customer_id: customerId,
        },
        (res: { success: boolean; error?: string }) => {
          if (res.success) {
            logger.log(`[useLiveSession] ✅ Successfully joined session: ${sid}`);
            setSessionId(sid);
            resolve();
          } else {
            logger.log(`[useLiveSession] ❌ Failed to join session: ${res.error}`);
            reject(new Error(res.error));
          }
        }
      );
    });
  }, [tenantId, role, vendorId, customerId]);

  // Cart management
  const addToCart = useCallback((item: CartItem) => {
    const newCart = {
      items: [...cart.items.filter((i) => i.product_id !== item.product_id), item],
    };
    setCart(newCart);

    if (socketRef.current && sessionId) {
      socketRef.current.emit('update_cart', {
        tenant_id: tenantId,
        session_id: sessionId,
        cart: newCart,
      });
    }
  }, [cart, sessionId, tenantId]);

  const removeFromCart = useCallback((productId: string) => {
    const newCart = {
      items: cart.items.filter((i) => i.product_id !== productId),
    };
    setCart(newCart);

    if (socketRef.current && sessionId) {
      socketRef.current.emit('update_cart', {
        tenant_id: tenantId,
        session_id: sessionId,
        cart: newCart,
      });
    }
  }, [cart, sessionId, tenantId]);

  const updateCartItem = useCallback((productId: string, quantity: number) => {
    const newCart = {
      items: cart.items.map((i) =>
        i.product_id === productId ? { ...i, quantity } : i
      ),
    };
    setCart(newCart);

    if (socketRef.current && sessionId) {
      socketRef.current.emit('update_cart', {
        tenant_id: tenantId,
        session_id: sessionId,
        cart: newCart,
      });
    }
  }, [cart, sessionId, tenantId]);

  const clearCart = useCallback(() => {
    const newCart = { items: [] };
    setCart(newCart);

    if (socketRef.current && sessionId) {
      socketRef.current.emit('update_cart', {
        tenant_id: tenantId,
        session_id: sessionId,
        cart: newCart,
      });
    }
  }, [sessionId, tenantId]);

  // Send message
  const sendMessage = useCallback((message: string, messageSuggestions?: any[]) => {
    if (!socketRef.current || !sessionId) return;

    const event = role === 'vendor' ? 'vendor_message' : 'customer_message';
    socketRef.current.emit(event, {
      session_id: sessionId,
      message,
      ...(messageSuggestions && { suggestions: messageSuggestions }),
    });
  }, [sessionId, role]);

  // Customer suggestion (bidirectional cart)
  const suggestProduct = useCallback((product: Omit<CustomerSuggestion, 'id' | 'timestamp' | 'status'>) => {
    if (!socketRef.current || !sessionId) return;

    const suggestion = {
      ...product,
      id: `suggestion-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    };

    socketRef.current.emit('customer_suggestion', {
      session_id: sessionId,
      suggestion,
    });
  }, [sessionId]);

  // Vendor response to suggestion
  const respondToSuggestion = useCallback((suggestionId: string, action: 'approve' | 'reject' | 'later') => {
    if (!socketRef.current || !sessionId) return;

    socketRef.current.emit('suggestion_response', {
      session_id: sessionId,
      suggestion_id: suggestionId,
      action,
    });
  }, [sessionId]);

  // E-commerce cart integration
  // Vendor sends cart action to client (add/remove/update product)
  const sendCartAction = useCallback((action: EcommerceCartAction) => {
    if (!socketRef.current) {
      logger.error('[useLiveSession] ❌ sendCartAction: socket not connected');
      return;
    }
    if (!sessionId) {
      logger.error('[useLiveSession] ❌ sendCartAction: no sessionId');
      return;
    }

    logger.log('[useLiveSession] 🚀 Sending cart_action:', action.type, action);
    socketRef.current.emit('cart_action', {
      session_id: sessionId,
      action,
    });
  }, [sessionId]);

  // Client syncs cart state to vendor
  // Accepte un sessionId optionnel pour forcer l'utilisation d'un ID spécifique (utile juste après joinSession)
  const syncCartState = useCallback((items: EcommerceCartItem[], forceSessionId?: string) => {
    const effectiveSessionId = forceSessionId || sessionIdRef.current || sessionId;

    if (!socketRef.current) {
      return;
    }
    if (!effectiveSessionId) {
      return;
    }
    if (!socketRef.current.connected) {
      return;
    }

    socketRef.current.emit('cart_state', {
      session_id: effectiveSessionId,
      items,
    });
  }, [sessionId]);

  // Session control
  const requestCheckout = useCallback(async () => {
    if (!socketRef.current || !sessionId) return;

    socketRef.current.emit('request_checkout', {
      tenant_id: tenantId,
      session_id: sessionId,
    });
  }, [tenantId, sessionId]);

  const completeSession = useCallback(async (cartItems?: EcommerceCartItem[]): Promise<{
    success: boolean;
    orderId?: string;
    orderNumber?: string;
    totalCents?: number;
    error?: string;
  }> => {
    if (!socketRef.current || !sessionId) {
      return { success: false, error: 'Socket non connecté ou session manquante' };
    }

    return new Promise((resolve) => {
      // Convertir les items du panier au format attendu par le backend
      const formattedItems = cartItems?.map(item => ({
        product_id: item.product?.id || item.product?.product_id,
        name: item.product?.name || item.product?.product_name,
        quantity: item.quantity,
        price_cents: item.unit_price_cents,
        vat_rate: item.product?.vat_rate || 0.06,
        prepared: item.prepared,
        addedBy: item.addedBy,
        addedAt: item.addedAt,
      }));

      socketRef.current!.emit(
        'complete_session',
        {
          tenant_id: tenantId,
          session_id: sessionId,
          cart_items: formattedItems,
        },
        (response: {
          success: boolean;
          order_id?: string;
          order_number?: string;
          total_cents?: number;
          error?: string;
        }) => {
          if (response.success) {
            resolve({
              success: true,
              orderId: response.order_id,
              orderNumber: response.order_number,
              totalCents: response.total_cents,
            });
          } else {
            resolve({
              success: false,
              error: response.error || 'Erreur lors de la finalisation',
            });
          }
        }
      );
    });
  }, [tenantId, sessionId]);

  const cancelSession = useCallback(async () => {
    if (!socketRef.current || !sessionId) return;

    socketRef.current.emit('update_session_status', {
      tenant_id: tenantId,
      session_id: sessionId,
      status: 'cancelled',
    });
  }, [tenantId, sessionId]);

  // Get cancellation fees before cancelling
  const getCancellationFees = useCallback(async (): Promise<{
    feesCents: number;
    durationMinutes: number;
    hasItems: boolean;
    feesEuros: string;
  } | null> => {
    if (!socketRef.current || !sessionId) return null;

    return new Promise((resolve) => {
      socketRef.current!.emit(
        'get_cancellation_fees',
        {
          tenant_id: tenantId,
          session_id: sessionId,
        },
        (response: any) => {
          if (response.success) {
            resolve({
              feesCents: response.fees_cents,
              durationMinutes: response.duration_minutes,
              hasItems: response.has_items,
              feesEuros: response.fees_euros,
            });
          } else {
            resolve(null);
          }
        },
      );
    });
  }, [tenantId, sessionId]);

  // Cancel session with fees (for customer)
  const cancelSessionWithFees = useCallback(async (
    cancelledBy: 'customer' | 'vendor' = 'customer',
    reason: 'manual' | 'technical' = 'manual',
  ): Promise<{
    success: boolean;
    feesCents: number;
    durationMinutes: number;
  } | null> => {
    if (!socketRef.current || !sessionId) return null;

    return new Promise((resolve) => {
      socketRef.current!.emit(
        'cancel_session_with_fees',
        {
          tenant_id: tenantId,
          session_id: sessionId,
          cancelled_by: cancelledBy,
          reason,
        },
        (response: any) => {
          if (response.success) {
            resolve({
              success: true,
              feesCents: response.fees_cents,
              durationMinutes: response.duration_minutes,
            });
          } else {
            resolve(null);
          }
        },
      );
    });
  }, [tenantId, sessionId]);

  // Finalize session without customer (for vendor when customer disconnected)
  const finalizeSessionWithoutCustomer = useCallback(async (): Promise<{
    success: boolean;
    orderId: string;
    orderNumber: string;
  } | null> => {
    if (!socketRef.current || !sessionId) return null;

    return new Promise((resolve) => {
      socketRef.current!.emit(
        'finalize_session_without_customer',
        {
          tenant_id: tenantId,
          session_id: sessionId,
        },
        (response: any) => {
          if (response.success) {
            resolve({
              success: true,
              orderId: response.order_id,
              orderNumber: response.order_number,
            });
          } else {
            resolve(null);
          }
        },
      );
    });
  }, [tenantId, sessionId]);

  // Vendeur termine sa session (imprime ticket articles préparés)
  const endVendorSession = useCallback(async (vendorName?: string): Promise<{
    success: boolean;
    preparedItemsCount: number;
    totalPreparedCents: number;
    printSuccess?: boolean;
    error?: string;
  } | null> => {
    if (!socketRef.current || !sessionId) return null;

    return new Promise((resolve) => {
      socketRef.current!.emit(
        'end_vendor_session',
        {
          tenant_id: tenantId,
          session_id: sessionId,
          vendor_name: vendorName,
        },
        (response: any) => {
          if (response.success) {
            resolve({
              success: true,
              preparedItemsCount: response.prepared_items_count,
              totalPreparedCents: response.total_prepared_cents,
              printSuccess: response.print_success,
            });
          } else {
            resolve({
              success: false,
              preparedItemsCount: 0,
              totalPreparedCents: 0,
              error: response.error,
            });
          }
        },
      );
    });
  }, [tenantId, sessionId]);

  // Track mount state for StrictMode handling
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Only disconnect if we're truly unmounting (not StrictMode remount)
      // Use setTimeout to allow StrictMode to remount before cleanup
      const socket = socketRef.current;
      setTimeout(() => {
        if (!isMountedRef.current && socket) {
          socket.disconnect();
        }
      }, 100);
    };
  }, []);

  return {
    isConnected,
    connect,
    disconnect,
    isAvailable,
    setAvailable,
    availableVendors,
    // Active sessions (vendor can rejoin)
    activeSessions,
    refreshActiveSessions,
    queue,
    queuePosition,
    estimatedWaitMinutes,
    joinQueue,
    leaveQueue,
    acceptQueueEntry,
    session,
    sessionId,
    joinSession,
    cart,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    messages,
    sendMessage,
    suggestions,
    suggestProduct,
    respondToSuggestion,
    // E-commerce cart integration
    sendCartAction,
    syncCartState,
    vendorCartState,
    // Session control
    requestCheckout,
    completeSession,
    cancelSession,
    // Session control with fees
    getCancellationFees,
    cancelSessionWithFees,
    finalizeSessionWithoutCustomer,
    // Vendor ends session (prints prepared items ticket)
    endVendorSession,
  };
}
